import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { isRC } from '@/utils';
import cx from 'classnames';
import Styles from './index.module.scss';

export interface IDotLottie {
  className?: string;
  src: string;
  autoplay?: boolean;
  loop?: boolean;
  onComplete?: () => void;
}

export interface IDotLottieMethods {
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSeek: (frame: number) => void;
}

const DotLottie = forwardRef(({
  className,
  src,
  autoplay = true,
  loop = true,
  onComplete,
}: IDotLottie, ref) => {
  useImperativeHandle(ref, () => ({
    onPlay: play,
    onPause: pause,
    onStop: stop,
    onSeek: seek,
  }));

  const [dotLottie, setDotLottie] = useState<any>();
  const [DotLottieReact, setDotLottieReact] = useState<any>(null);

  const dotLottieRefCallback = (dotLottie: any) => {
    setDotLottie(dotLottie);
  };

  const play = () => {
    const isLoaded = dotLottie?.isLoaded;
    const isReady = dotLottie?.isReady;
    dotLottie?.play();
    return isLoaded && isReady;
  }

  const pause = () => {
    dotLottie?.pause();
  }

  const stop = () => {
    dotLottie?.stop();
  }

  const seek = (frame: number) => {
    dotLottie?.setFrame(frame);
  }

  useEffect(() => {
    if (!isRC) {
      import('@lottiefiles/dotlottie-react').then(module => {
        setDotLottieReact(() => module.DotLottieReact);
      });
    }
  }, []);

  useEffect(() => {
    if (dotLottie) {
      dotLottie.addEventListener('complete', onComplete);
    }

    return () => {
      if (dotLottie) {
        dotLottie.removeEventListener('complete', onComplete);
      }
    };
  }, [dotLottie]);

  if (!DotLottieReact) {
    return null;
  }

  return (
    <div className={cx(Styles.root, className)}>
      <DotLottieReact
        className={Styles.lottie}
        src={src}
        autoplay={autoplay}
        loop={loop}
        dotLottieRefCallback={dotLottieRefCallback}
      />
    </div>
  );
});

DotLottie.displayName = 'DotLottie';

export default DotLottie;
